import { z } from 'zod';
import { TransactionStatusCountsSchema } from './seller';
import { TransactionSchema, CommissionFieldsShape } from '@/schemas/models/transaction';
import { UserMinimalSchema } from '@/schemas/models/user';
import { ErrorResponseSchema } from '@/schemas/common/base';
import { nonNegativeIntValidator, nonNegativeIntValidatorNullable, idValidator, priceValidatorCoerce } from '@/schemas/common/validators';

/**
 * User photo schema for buyer transactions
 * Allows empty strings for users without photos
 */
export const BuyerTransactionUserPhotoSchema = z.object({
  /** Full-size photo URL (can be empty string if no photo) */
  url: z.string(),
  /** Thumbnail photo URL (can be empty string if no photo) */
  thumbUrl: z.string(),
});

export type BuyerTransactionUserPhoto = z.infer<typeof BuyerTransactionUserPhotoSchema>;

/**
 * Listing photo schema for buyer transactions
 * Includes both full URL and thumbnail URL
 */
export const BuyerTransactionListingPhotoSchema = z.object({
  /** Photo URL */
  url: z.string(),
  /** Thumbnail URL */
  thumbUrl: z.string(),
});

export type BuyerTransactionListingPhoto = z.infer<typeof BuyerTransactionListingPhotoSchema>;

/**
 * GET /api/transactions/buyer
 * Fetches all transactions where the authenticated user is the buyer
 */

/**
 * Buyer nested within transaction
 * Contains buyer ID and read status
 */
export const TransactionBuyerSchema = z.object({
  /** Buyer user ID */
  id: idValidator,
  /** Whether buyer has read the transaction (0 or 1) */
  read: z.union([z.number().int().min(0).max(1), z.boolean()]).nullable(),
});

export type TransactionBuyer = z.infer<typeof TransactionBuyerSchema>;

/**
 * Seller information in buyer transaction
 * Extends minimal user with additional fields and photos
 */
export const BuyerTransactionSellerSchema = UserMinimalSchema.extend({
  /**
   * Seller email address, or '' when the account has none. USERS.email is
   * nullable; see the buyer-email note on the seller schema.
   */
  email: z.string(),
  /** User trust level (0-10 scale) */
  trustLevel: nonNegativeIntValidator,
  /** Whether user has uploaded a profile photo */
  hasPhoto: z.number().int().min(0).max(1),
  /** User profile photos */
  photos: BuyerTransactionUserPhotoSchema,
});

export type BuyerTransactionSeller = z.infer<typeof BuyerTransactionSellerSchema>;

/**
 * Full listing details in buyer transaction
 * Extends minimal listing with all fields and nested seller
 */
export const BuyerTransactionListingSchema = z.object({
  /** Listing ID */
  id: idValidator,
  /** Listing title */
  title: z.string().max(254),
  /** Listing price (coerced from string to number) */
  price: priceValidatorCoerce,
  /** Photo count (0 or more) */
  hasPhoto: z.number().int().min(0),
  /** User ID of listing owner */
  userId: idValidator.nullable(),
  /** Listing description text */
  text: z.string().max(5000).nullable(),
  /** Category ID */
  categoryId: idValidator,
  /** Category group ID */
  categoryGroupId: z.number().int().min(0),
  /** Listing status (active/inactive/sold) */
  status: z.number().int().min(0).max(255),
  /** Number of photos */
  photoCount: z.number().int().min(0),
  /** Array of listing photos */
  photos: z.array(BuyerTransactionListingPhotoSchema),
  /** Whether item is shippable */
  shippable: z.number().int().min(0).max(1).nullable(),
  /** Data source identifier (1=organic, 40=CJ Dropshipping, etc.) */
  dataSourceId: z.number().int().nullable().optional(),
  /** Item condition (itemCondition field from database) */
  condition: z.union([z.number().int(), z.string()]).nullable(),
  /** Location text (city, state) */
  locationText: z.string().max(125).nullable(),
  /** Listing creation timestamp */
  createdDate: z.coerce.date(),
  /** Nested seller information */
  user: BuyerTransactionSellerSchema.nullable(),
});

export type BuyerTransactionListing = z.infer<typeof BuyerTransactionListingSchema>;

/**
 * Buyer transaction schema
 * Full transaction details with seller and listing information
 */
export const BuyerTransactionSchema = TransactionSchema.extend({
  /** Seller user ID (optional - included in nested seller object) */
  sellerId: z.union([z.number().int(), z.bigint().transform(Number), z.string().transform(Number)]).nullable().optional(),
  /** Buyer user ID (optional - included in nested buyer object) */
  buyerId: z.union([z.number().int(), z.bigint().transform(Number), z.string().transform(Number)]).nullable().optional(),
  /** Transaction creation timestamp alias */
  createdAt: z.coerce.date(),
  /** Whether seller has read the transaction (0 or 1) */
  read: z.union([z.number().int().min(0).max(1), z.boolean()]).nullable(),
  /** Whether buyer has read the transaction (0 or 1) - optional since not returned in response */
  buyerRead: z.union([z.number().int().min(0).max(1), z.boolean()]).nullable().optional(),
  /** Associated payment ID */
  paymentId: z.number().int().nullable(),
  /** Associated shipping ID */
  shippingId: z.number().int().nullable(),
  /** Associated commission payment ID */
  commissionId: z.number().int().nullable(),
  ...CommissionFieldsShape,
  /**
   * Sales tax inside `price` (LISTING_TRANSACTION.taxAmount). Null on a sale
   * from before checkout quoted tax. Optional so a client parse of an older
   * response still passes.
   */
  taxAmount: z.number().min(0).nullable().optional(),
  /** Buyer information */
  buyer: TransactionBuyerSchema,
  /** Seller information */
  seller: BuyerTransactionSellerSchema.nullable(),
  /** Full listing details with nested seller */
  listing: BuyerTransactionListingSchema.nullable(),
  /** Coupon applied to this transaction, if any */
  coupon: z.object({
    code: z.string(),
    name: z.string(),
    discountAmount: z.number(),
  }).nullable().optional(),
});

export type BuyerTransaction = z.infer<typeof BuyerTransactionSchema>;

/**
 * Success response schema for buyer transactions
 * Returns array of transactions where user is the buyer
 */
/**
 * Query parameters for GET /api/transactions/buyer.
 *
 * Paging is OPT-IN, exactly as on the seller side: without `page` this returns
 * every purchase, which is what AppSessionProvider still loads on sign-in.
 */
export const GetBuyerTransactionsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional().default(50),
  status: z.coerce.number().int().min(0).max(7).optional(),
  q: z.string().trim().max(200).optional(),
});

export type GetBuyerTransactionsQuery = z.infer<typeof GetBuyerTransactionsQuerySchema>;

export const GetBuyerTransactionsSuccessSchema = z.object({
  success: z.literal(true),
  /** Array of buyer transactions */
  transactions: z.array(BuyerTransactionSchema),
  /** Present only for a paged request; absent keeps the legacy shape intact. */
  total: z.number().optional(),
  page: z.number().optional(),
  limit: z.number().optional(),
  hasMore: z.boolean().optional(),
  counts: TransactionStatusCountsSchema.optional(),
});

export type GetBuyerTransactionsSuccess = z.infer<typeof GetBuyerTransactionsSuccessSchema>;

/**
 * Complete response schema for buyer transactions
 * Discriminated union of success and error responses
 */
export const GetBuyerTransactionsResponseSchema = z.discriminatedUnion('success', [
  GetBuyerTransactionsSuccessSchema,
  ErrorResponseSchema,
]);

export type GetBuyerTransactionsResponse = z.infer<typeof GetBuyerTransactionsResponseSchema>;
